import { useMemo, useState } from 'react';
import { Loader2Icon, SearchIcon } from 'lucide-react';
import type { AudioCanonicalTranscriptDTO } from '@shared/schemas/audio-transcription';
import {
  Artifact,
  ArtifactContent,
  ArtifactDescription,
  ArtifactHeader,
  ArtifactTitle,
  ArtifactClose,
} from '@/components/ai-elements/artifact';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { getChatErrorMessage } from '../chat-errors';
import { useAudioTranscript } from '../hooks/useAudioTranscription';

type ChatAudioTranscriptPanelProps = {
  batchId: string;
  filename: string;
  jobId: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
};

type SpeakerBlock = {
  key: string;
  speakerId: string | null;
  speakerName: string;
  texts: string[];
};

function buildSpeakerBlocks(transcript: AudioCanonicalTranscriptDTO): SpeakerBlock[] {
  const speakerNames = new Map(transcript.speakers.map((speaker) => [speaker.id, speaker.displayName]));
  const blocks: SpeakerBlock[] = [];

  for (const segment of transcript.segments) {
    const text = segment.text.trim();
    if (!text) {
      continue;
    }

    const displayText = segment.uncertain ? `${text} [passage incertain]` : text;
    const last = blocks.at(-1);
    if (last && last.speakerId === segment.speakerId) {
      last.texts.push(displayText);
      continue;
    }

    blocks.push({
      key: segment.id,
      speakerId: segment.speakerId,
      speakerName: segment.speakerId
        ? (speakerNames.get(segment.speakerId) ?? 'Intervenant')
        : 'Non identifié',
      texts: [displayText],
    });
  }

  return blocks;
}

export function ChatAudioTranscriptPanel({
  batchId,
  filename,
  jobId,
  onOpenChange,
  open,
}: ChatAudioTranscriptPanelProps) {
  const [query, setQuery] = useState('');
  const transcriptQuery = useAudioTranscript({
    batchId,
    enabled: open,
    jobId,
  });
  const transcript = transcriptQuery.data?.transcript ?? null;
  const blocks = useMemo(
    () => (transcript ? buildSpeakerBlocks(transcript) : []),
    [transcript],
  );
  const normalizedQuery = query.trim().toLocaleLowerCase('fr');
  const visibleBlocks = useMemo(() => {
    if (!normalizedQuery) {
      return blocks;
    }

    return blocks.filter((block) => (
      block.speakerName.toLocaleLowerCase('fr').includes(normalizedQuery)
      || block.texts.some((text) => text.toLocaleLowerCase('fr').includes(normalizedQuery))
    ));
  }, [blocks, normalizedQuery]);

  return (
    <Sheet onOpenChange={onOpenChange} open={open}>
      <SheetContent
        className="w-full p-0 sm:max-w-xl"
        data-testid="audio-transcript-panel"
        showCloseButton={false}
        side="right"
      >
        <Artifact className="h-full rounded-none border-0 shadow-none">
          <SheetTitle className="sr-only">{filename}</SheetTitle>
          <ArtifactHeader className="h-[3.25rem] border-border/50 bg-muted/20 px-4">
            <div className="min-w-0">
              <ArtifactTitle className="truncate">{filename}</ArtifactTitle>
              <ArtifactDescription className="truncate">
                Transcription consultable — sans lecteur audio
              </ArtifactDescription>
            </div>
            <ArtifactClose
              aria-label="Fermer la transcription"
              onClick={() => onOpenChange(false)}
            />
          </ArtifactHeader>
          <ArtifactContent className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-4">
            {transcriptQuery.isPending ? (
              <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
                <Loader2Icon className="mr-2 size-4 animate-spin motion-reduce:animate-none" aria-hidden />
                Chargement de la transcription…
              </div>
            ) : null}

            {transcriptQuery.isError ? (
              <Alert variant="destructive">
                <AlertTitle>Impossible d’ouvrir la transcription</AlertTitle>
                <AlertDescription className="flex flex-col gap-3">
                  <span>{getChatErrorMessage(transcriptQuery.error)}</span>
                  <Button
                    onClick={() => void transcriptQuery.refetch()}
                    size="sm"
                    type="button"
                    variant="outline"
                  >
                    Réessayer
                  </Button>
                </AlertDescription>
              </Alert>
            ) : null}

            {transcript && blocks.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucun texte transcrit n’est disponible pour ce fichier.</p>
            ) : null}

            {transcript && blocks.length > 0 ? (
              <>
                <div className="relative">
                  <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    aria-label="Rechercher dans la transcription"
                    className="pl-9"
                    onChange={(event) => setQuery(event.currentTarget.value)}
                    placeholder="Rechercher un intervenant ou un passage"
                    value={query}
                  />
                </div>
                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
                  {visibleBlocks.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Aucun passage ne correspond à la recherche.</p>
                  ) : visibleBlocks.map((block) => (
                    <section key={block.key} className="space-y-1.5">
                      <h3 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                        {block.speakerName}
                      </h3>
                      {block.texts.map((text, index) => (
                        <p
                          className={cn(
                            'text-sm leading-6 text-foreground',
                            text.includes('[passage incertain]') && 'text-muted-foreground',
                          )}
                          key={`${block.key}-${index}`}
                        >
                          {text}
                        </p>
                      ))}
                    </section>
                  ))}
                </div>
              </>
            ) : null}
          </ArtifactContent>
        </Artifact>
      </SheetContent>
    </Sheet>
  );
}
